// Plan 23b, Task 1.1 — Toast Notification primitive.
// Behavior contract: docs/design/components.md §9 lines 329–373.
// Animation: uses Plan 20a createToastScoreboardSlideHook (toast-in / toast-out).

import type { UIComponentHandle, UIComponentConfig } from './types';
import { createToastScoreboardSlideHook } from '../vfx/toast-scoreboard-slide';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ToastType = 'kill' | 'trophy' | 'item' | 'positive' | 'join' | 'leave';

export interface ToastStackOpts {
  /** Maximum number of simultaneously visible toasts. Default: 3. */
  maxVisible?: number;
  /** Auto-dismiss delay in milliseconds. Default: 4000. */
  dismissMs?: number;
}

export interface ToastOpts {
  type: ToastType;
  message: string;
  icon?: HTMLElement;
}

export interface ToastStackHandle extends UIComponentHandle {
  push(opts: ToastOpts): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Design token constants — values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.panel-bg — fully opaque panel background for toasts */
const PANEL_BG = '#1E2332';

/** layout.toast-width */
const TOAST_WIDTH = '320px';

/** border-radius.md */
const BORDER_RADIUS = '8px';

/** spacing.md — padding inside each toast */
const PADDING = '16px';

/** shadow.toast fallback */
const SHADOW_TOAST = '0 4px 24px rgba(0,0,0,0.55)';

/** spacing.lg — distance from top/right edges */
const EDGE_OFFSET = '24px';

/** Left border color per toast type — all values from tokens.json */
const BORDER_COLOR: Record<ToastType, string> = {
  kill: '#D94040', // color.button.danger.base
  trophy: '#FFD700', // color.hud.key-gold
  item: '#4A8ED9', // color.button.secondary.base
  positive: '#5CAD40', // color.button.success.base
  join: '#4A8ED9', // color.button.secondary.base
  leave: '#707888', // color.text.muted
};

/** CSS var token names for left border (with fallback values) */
const BORDER_COLOR_VAR: Record<ToastType, string> = {
  kill: 'var(--color-button-danger-base, #D94040)',
  trophy: 'var(--color-hud-key-gold, #FFD700)',
  item: 'var(--color-button-secondary-base, #4A8ED9)',
  positive: 'var(--color-button-success-base, #5CAD40)',
  join: 'var(--color-button-secondary-base, #4A8ED9)',
  leave: 'var(--color-text-muted, #707888)',
};

/** Exit animation duration (matches toast-scoreboard-slide toast-out = 150ms) */
const EXIT_DURATION_MS = 150;

// ─────────────────────────────────────────────────────────────────────────────
// Internal toast record
// ─────────────────────────────────────────────────────────────────────────────

interface ToastEntry {
  element: HTMLElement;
  dismissTimerId: ReturnType<typeof setTimeout> | null;
  isExiting: boolean;
  /** Entrance hook — must be disposed in ALL removal paths. */
  entranceHook: ReturnType<typeof createToastScoreboardSlideHook>;
  /** Exit animation timer ID — stored so it can be cleared on early dispose. */
  exitTimerId?: ReturnType<typeof setTimeout>;
  /** Exit hook — stored so it can be disposed on early dispose. */
  exitHook?: ReturnType<typeof createToastScoreboardSlideHook>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createToastStack(config: UIComponentConfig & ToastStackOpts): ToastStackHandle {
  const { gsapRegistry, mount, maxVisible = 3, dismissMs = 4000 } = config;

  let disposed = false;

  /** Ordered list of active toasts — index 0 is oldest. */
  const toasts: ToastEntry[] = [];

  // ── Build the stack container ─────────────────────────────────────────────

  const container = document.createElement('div');
  container.className = 'ui-toast-stack';
  container.style.cssText = [
    // Fixed top-right, spacing.lg from edges
    'position: fixed',
    `top: ${EDGE_OFFSET}`,
    `right: ${EDGE_OFFSET}`,
    'z-index: 1000',
    // Stack toasts vertically, gap = spacing.sm (8px)
    'display: flex',
    'flex-direction: column',
    'gap: var(--spacing-sm, 8px)',
    // Pointer events off on the container; individual toasts re-enable
    'pointer-events: none',
  ].join('; ');

  if (mount) {
    mount.appendChild(container);
  }

  // ── Internal helpers ─────────────────────────────────────────────────────

  /** Immediately remove a toast from DOM and the internal list (no animation). */
  function removeImmediate(entry: ToastEntry): void {
    if (entry.dismissTimerId !== null) {
      clearTimeout(entry.dismissTimerId);
      entry.dismissTimerId = null;
    }
    // Clear exit timer and dispose exit hook if still in-flight
    if (entry.exitTimerId !== undefined) {
      clearTimeout(entry.exitTimerId);
      entry.exitTimerId = undefined;
    }
    if (entry.exitHook !== undefined) {
      entry.exitHook.dispose();
      entry.exitHook = undefined;
    }
    // Dispose entrance hook — HF-1 fix
    entry.entranceHook.dispose();
    if (entry.element.parentNode) {
      entry.element.parentNode.removeChild(entry.element);
    }
    const idx = toasts.indexOf(entry);
    if (idx !== -1) {
      toasts.splice(idx, 1);
    }
  }

  /**
   * Animate a toast out via the toast-scoreboard-slide hook, then remove from DOM.
   * Each toast gets its own hook instance so concurrent exits don't interfere.
   */
  function animateOut(entry: ToastEntry): void {
    if (entry.isExiting) return;
    entry.isExiting = true;

    // Cancel pending auto-dismiss timer
    if (entry.dismissTimerId !== null) {
      clearTimeout(entry.dismissTimerId);
      entry.dismissTimerId = null;
    }

    // Dispose the entrance hook now that exit is starting — HF-1 fix
    entry.entranceHook.dispose();

    // Create a dedicated hook instance for this toast's exit animation
    const exitHook = createToastScoreboardSlideHook({ gsapRegistry });
    entry.exitHook = exitHook;
    exitHook.play(entry.element, { kind: 'toast-out' });

    // Remove from DOM after exit animation completes.
    // Store the timer ID so dispose() can cancel it if it fires early — HF-2 fix.
    // We use setTimeout rather than tl.call() here to avoid the GSAP sentinel
    // VIRTUAL_DURATION issue (see vfx/types.ts comment on sentinel tween).
    entry.exitTimerId = setTimeout(() => {
      entry.exitTimerId = undefined;
      exitHook.dispose();
      entry.exitHook = undefined;
      if (entry.element.parentNode) {
        entry.element.parentNode.removeChild(entry.element);
      }
      const idx = toasts.indexOf(entry);
      if (idx !== -1) {
        toasts.splice(idx, 1);
      }
    }, EXIT_DURATION_MS);
  }

  // ── push ─────────────────────────────────────────────────────────────────

  function push(opts: ToastOpts): void {
    if (disposed) return;

    // Enforce max-visible: instantly remove oldest toasts until we have room
    while (toasts.length >= maxVisible) {
      const oldest = toasts[0];
      if (oldest) {
        removeImmediate(oldest);
      }
    }

    // ── Build toast element ───────────────────────────────────────────────

    const toastEl = document.createElement('div');
    toastEl.setAttribute('data-toast', opts.type);
    toastEl.style.cssText = [
      `width: var(--layout-toast-width, ${TOAST_WIDTH})`,
      `background-color: var(--color-ui-panel-bg, ${PANEL_BG})`,
      `border-radius: var(--border-radius-md, ${BORDER_RADIUS})`,
      `padding: var(--spacing-md, ${PADDING})`,
      `box-shadow: var(--shadow-toast, ${SHADOW_TOAST})`,
      // 4px left accent border, colored per type
      `border-left: 4px solid ${BORDER_COLOR_VAR[opts.type]}`,
      // Explicit color for border-left-color (needed for test assertions)
      `border-left-color: ${BORDER_COLOR[opts.type]}`,
      // Layout: icon + message + dismiss button in a row
      'display: flex',
      'align-items: center',
      'gap: var(--spacing-sm, 8px)',
      'box-sizing: border-box',
      // Re-enable pointer events on the individual toast
      'pointer-events: auto',
      // Clip overflow during slide-in (xPercent can shift beyond right edge)
      'overflow: hidden',
    ].join('; ');

    // ── Optional icon ─────────────────────────────────────────────────────

    if (opts.icon) {
      opts.icon.style.cssText +=
        '; flex-shrink: 0; width: 20px; height: 20px; color: ' + BORDER_COLOR[opts.type];
      toastEl.appendChild(opts.icon);
    }

    // ── Message text ──────────────────────────────────────────────────────

    const msgEl = document.createElement('span');
    msgEl.className = 'toast-message';
    msgEl.textContent = opts.message;
    msgEl.style.cssText = [
      // typography.family.body
      "font-family: var(--typography-family-body, 'Nunito Sans', sans-serif)",
      // typography.size.caption = 14px
      'font-size: var(--typography-size-caption, 14px)',
      // color.text.primary
      'color: var(--color-text-primary, #FFFFFF)',
      'flex: 1',
      'min-width: 0',
      'overflow-wrap: break-word',
    ].join('; ');
    toastEl.appendChild(msgEl);

    // ── Dismiss button ────────────────────────────────────────────────────

    const dismissBtn = document.createElement('button');
    dismissBtn.setAttribute('data-dismiss', '');
    dismissBtn.setAttribute('aria-label', 'Dismiss notification');
    dismissBtn.textContent = '×';
    dismissBtn.style.cssText = [
      // color.text.muted
      'color: var(--color-text-muted, #707888)',
      'font-size: 18px',
      'background: none',
      'border: 1px solid transparent',
      'cursor: pointer',
      'padding: 0',
      'width: 44px',
      'height: 44px',
      'display: grid',
      'place-items: center',
      'flex-shrink: 0',
      'line-height: 1',
      'touch-action: manipulation',
    ].join('; ');
    toastEl.appendChild(dismissBtn);

    // ── Entrance animation ────────────────────────────────────────────────
    // Create a fresh hook instance per toast (one per active toast).
    // Stored on the entry so ALL removal paths can dispose it — HF-1 fix.
    const entranceHook = createToastScoreboardSlideHook({ gsapRegistry });
    entranceHook.play(toastEl, { kind: 'toast-in' });

    // ── Register entry ────────────────────────────────────────────────────

    const entry: ToastEntry = {
      element: toastEl,
      dismissTimerId: null,
      isExiting: false,
      entranceHook,
    };

    // ── Dismiss button click ──────────────────────────────────────────────

    dismissBtn.addEventListener('click', () => {
      if (entry.isExiting) return;
      animateOut(entry);
    });

    // ── Mount to container ────────────────────────────────────────────────

    container.appendChild(toastEl);
    toasts.push(entry);

    // ── Auto-dismiss timer ────────────────────────────────────────────────
    // Use setTimeout (not tl.call) so vi.useFakeTimers() controls it in tests.
    // animateOut() disposes the entrance hook — no manual hook.dispose() here.
    entry.dismissTimerId = setTimeout(() => {
      entry.dismissTimerId = null;
      if (!entry.isExiting && !disposed) {
        animateOut(entry);
      }
    }, dismissMs);
  }

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Immediately remove all active toasts (no exit animation)
    // Iterate over a copy since removeImmediate mutates the array
    const entries = [...toasts];
    for (const entry of entries) {
      removeImmediate(entry);
    }

    // Detach the container from DOM
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }

  return {
    element: container,
    push,
    dispose,
  };
}
